export const PLANS = {
  starter: {
    id: "starter",
    name: "Starter",
    price: 1000,
    coupons: ["TOP30"] as const,
    referralBonus: 0,
    color: "from-slate-500 to-slate-700",
  },
  standard: {
    id: "standard",
    name: "Standard",
    price: 2000,
    coupons: ["TOP20", "TOP30"] as const,
    referralBonus: 500,
    color: "from-emerald-500 to-emerald-700",
  },
  premium: {
    id: "premium",
    name: "Premium",
    price: 5000,
    coupons: ["TOP10", "TOP20", "TOP30"] as const,
    referralBonus: 2000,
    color: "from-amber-500 to-amber-700",
  },
  bigboss: {
    id: "bigboss",
    name: "Big Boss",
    price: 10000,
    minPrice: 10000,
    coupons: ["TOP10", "TOP20", "TOP30"] as const,
    referralBonus: -1, // 30% de la mise (cf. BIGBOSS_REFERRAL_RATE)
    color: "from-fuchsia-500 to-violet-700",
  },
} as const;

export type PlanId = keyof typeof PLANS;

export const COUPON_TYPES = {
  TOP10: { matches: 10, requiredPlan: "premium", color: "bg-amber-500" },
  TOP20: { matches: 20, requiredPlan: "standard", color: "bg-emerald-500" },
  TOP30: { matches: 30, requiredPlan: "starter", color: "bg-slate-500" },
} as const;

export type CouponType = keyof typeof COUPON_TYPES;

/**
 * Catalogue des marchés de pari supportés.
 *
 * `category` regroupe les markets dans le BetTypeSheet en 3 onglets
 * (Vainqueur / Buts / Stats). `source` indique d'où vient la donnée
 * d'évaluation côté coupon-validate :
 *   - `fixture` → champs déjà présents dans /fixtures (score, halftime)
 *   - `statistics` → nécessite un appel /fixtures/statistics en plus
 */
export const BET_TYPES = {
  // === Vainqueur ===
  result:       { label: "1X2",            icon: "⚽", category: "winner", source: "fixture" },
  doubleChance: { label: "Double chance",  icon: "🛡️", category: "winner", source: "fixture" },
  dnb:          { label: "Draw No Bet",    icon: "🚫", category: "winner", source: "fixture" },
  htResult:     { label: "Mi-temps 1X2",   icon: "⏰", category: "winner", source: "fixture" },
  htft:         { label: "Mi-temps/fin",   icon: "⏱️", category: "winner", source: "fixture" },

  // === Buts ===
  score:        { label: "Score exact",    icon: "🎯", category: "goals",  source: "fixture" },
  goals:        { label: "+/- buts",       icon: "📊", category: "goals",  source: "fixture" },
  btts:         { label: "BTTS",           icon: "🥅", category: "goals",  source: "fixture" },
  htGoals:      { label: "+/- mi-temps",   icon: "⌚", category: "goals",  source: "fixture" },
  teamGoals:    { label: "+/- buts équipe", icon: "🎯", category: "goals",  source: "fixture" },

  // === Stats (post-match, via /fixtures/statistics) ===
  corners:      { label: "Corners",        icon: "🚩", category: "stats",  source: "statistics" },
  cards:        { label: "Cartons",        icon: "🟨", category: "stats",  source: "statistics" },
  shots:        { label: "Tirs",           icon: "⚡", category: "stats",  source: "statistics" },
  fouls:        { label: "Fautes",         icon: "✋", category: "stats",  source: "statistics" },
} as const;

export type BetType = keyof typeof BET_TYPES;
export type BetCategory = "winner" | "goals" | "stats";
export type BetSource = "fixture" | "statistics";

/** Catégories d'affichage dans le BetTypeSheet — ordre = ordre d'affichage. */
export const BET_CATEGORIES: Record<BetCategory, { label: string; types: BetType[] }> = {
  winner: { label: "Vainqueur", types: ["result", "doubleChance", "dnb", "htResult", "htft"] },
  goals:  { label: "Buts",      types: ["score", "goals", "btts", "htGoals", "teamGoals"] },
  stats:  { label: "Stats",     types: ["corners", "cards", "shots", "fouls"] },
};

/** Lines proposées pour les markets Over/Under. */
export const BET_LINES: Partial<Record<BetType, number[]>> = {
  goals:     [1.5, 2.5, 3.5],
  htGoals:   [0.5, 1.5, 2.5],
  teamGoals: [0.5, 1.5, 2.5],
  corners:   [8.5, 9.5, 10.5, 11.5],
  cards:     [3.5, 4.5, 5.5],
  shots:     [12.5, 13.5, 14.5],
  fouls:     [19.5, 21.5, 23.5],
};

/**
 * Opérateurs mobile money supportés par PayDunya.
 * `id` est le slug exact attendu par l'API PayDunya (champ `withdraw_mode`).
 * `country` est l'ISO 3166-1 alpha-2 du pays.
 */
export interface OperatorMeta {
  id: string;
  name: string;
  country: string;
  emoji: string;
  color: string;
}

export const OPERATORS: OperatorMeta[] = [
  // Sénégal
  { id: "wave-senegal", country: "SN", name: "Wave", emoji: "🌊", color: "bg-sky-500" },
  { id: "orange-money-senegal", country: "SN", name: "Orange Money", emoji: "🟠", color: "bg-orange-500" },
  { id: "free-money-senegal", country: "SN", name: "Free Money", emoji: "🔵", color: "bg-blue-600" },
  { id: "expresso-senegal", country: "SN", name: "Expresso", emoji: "🟣", color: "bg-fuchsia-600" },

  // Côte d'Ivoire
  { id: "wave-ci", country: "CI", name: "Wave", emoji: "🌊", color: "bg-sky-500" },
  { id: "orange-money-ci", country: "CI", name: "Orange Money", emoji: "🟠", color: "bg-orange-500" },
  { id: "mtn-ci", country: "CI", name: "MTN Money", emoji: "🟡", color: "bg-yellow-500" },
  { id: "moov-ci", country: "CI", name: "Moov", emoji: "🔷", color: "bg-blue-500" },

  // Mali — PayDunya disburse ne supporte qu'Orange Money pour le Mali
  // (cf. https://developers.paydunya.com/doc/FR/api_deboursement)
  { id: "orange-money-mali", country: "ML", name: "Orange Money", emoji: "🟠", color: "bg-orange-500" },

  // Burkina Faso
  { id: "orange-money-burkina", country: "BF", name: "Orange Money", emoji: "🟠", color: "bg-orange-500" },
  { id: "moov-burkina-faso", country: "BF", name: "Moov", emoji: "🔷", color: "bg-blue-500" },

  // Bénin
  { id: "mtn-benin", country: "BJ", name: "MTN Money", emoji: "🟡", color: "bg-yellow-500" },
  { id: "moov-benin", country: "BJ", name: "Moov", emoji: "🔷", color: "bg-blue-500" },

  // Togo — Flooz n'est pas un slug PayDunya, le wallet Moov est exposé via "moov-togo"
  { id: "t-money-togo", country: "TG", name: "T-Money", emoji: "🟠", color: "bg-orange-500" },
  { id: "moov-togo", country: "TG", name: "Moov", emoji: "🔷", color: "bg-blue-500" },

  // Cameroun — PayDunya disburse ne supporte que MTN
  { id: "mtn-cameroun", country: "CM", name: "MTN MoMo", emoji: "🟡", color: "bg-yellow-500" },

  // Djamo (nouveau, disponible CI + SN sur l'API disburse)
  { id: "djamo-ci", country: "CI", name: "Djamo", emoji: "🟢", color: "bg-emerald-500" },
  { id: "djamo-sn", country: "SN", name: "Djamo", emoji: "🟢", color: "bg-emerald-500" },
];

export type Operator = string; // slug PayDunya, plus contraint à un set fini

export function operatorsForCountry(countryCode: string): OperatorMeta[] {
  return OPERATORS.filter((o) => o.country === countryCode);
}

export function findOperator(id: string): OperatorMeta | undefined {
  return OPERATORS.find((o) => o.id === id);
}

export const MIN_DEPOSIT = 500;
export const MIN_WITHDRAW = 1000;
export const MIN_BIGBOSS = 10000;
/** Taux du bonus parrain pour le plan Big Boss (proportion de la mise du filleul). */
export const BIGBOSS_REFERRAL_RATE = 0.3;

/**
 * Multiplicateur de gain appliqué sur un coupon gagnant :
 *   gain = mise_abonnement × COUPON_PAYOUT_MULTIPLIER
 * Ex. abonnement Starter 1 000 FCFA → 1 000 000 FCFA si tous les pronos passent.
 * Doit rester aligné avec COUPON_PAYOUT_MULTIPLIER côté
 * supabase/functions/coupon-validate/index.ts.
 */
export const COUPON_PAYOUT_MULTIPLIER = 1000;
export const GIFT_REFERRALS_REQUIRED = 3;
export const GIFT_TOP_RANK = 100;
/** Nombre de jours dont dispose un nouveau user pour atteindre les 3 parrainages payants. */
export const GIFT_GRACE_DAYS = 14;
export const COUPON_DAILY_LIMIT_PER_TYPE = 1;
export const BIGBOSS_REFUND_MONTHS = 12;

/* ============================================================================
 * Frais PayDunya (mobile money)
 * Le client paie les frais — UI transparente sur le total prélevé.
 * Côté dépôt : grossAmount = amount + fee   → wallet crédité de `amount`.
 * Côté retrait : on prélève (amount + fee)  → user reçoit `amount`.
 * ========================================================================== */
export const PAYDUNYA_FEE_RATE = 0.04; // 4%
export const PAYDUNYA_FEE_MIN = 100;
export const PAYDUNYA_FEE_MAX = 600;

/** Frais à appliquer (arrondi entier, bornes min/max). */
export function calculateFee(amount: number): number {
  if (!amount || amount <= 0) return 0;
  const raw = Math.round(amount * PAYDUNYA_FEE_RATE);
  return Math.max(PAYDUNYA_FEE_MIN, Math.min(PAYDUNYA_FEE_MAX, raw));
}

/** Total prélevé (dépôt : client paie ça ; retrait : wallet débité de ça). */
export function totalWithFee(amount: number): number {
  return amount + calculateFee(amount);
}

/* ============================================================================
 * Limites de sécurité retrait
 * ========================================================================== */
export const MAX_WITHDRAW_PER_TX = 500_000;
export const DAILY_WITHDRAW_LIMIT_PER_USER = 500_000;
export const ADMIN_MASS_VALIDATE_CONFIRM_THRESHOLD = 500_000;
